import React from "react";
import { RouteComponentProps } from "react-router";
import { Box, Button, Container, TextField } from "@material-ui/core";
import logo from "../../../assets/ReadleeLogoRed.png";
import "./LoginScreen.css";
import FirebaseService from "../../../services/firebase/FirebaseService";
import { Link } from "react-router-dom";
import { RootState } from "../../../state/configureStore";
import { connect } from "react-redux";
import Loading from "../../Loading/Loading";
import LogService from "../../../services/LogService";
import InformationModal from "../../Modals/InformationModal";
import { closeInformationModal, showErrorModal } from "../../Modals/InformationModalReducer";
import ButtonWithLoading from "../../Loading/ButtonWithLoading";
import GoogleIcon from "../../../assets/icons/google-icon.jpeg";
import CleverIcon from "../../../assets/icons/cleverIcon.png";
import LoadingButton from "../../Loading/LoadingButton";
import { EMAIL_MAX_LENGTH } from "../../../utils/stringFormatting";
import queryString from "query-string";

type LoginState = {
  userName: string;
  password: string;
  error: string;
  googleLoadingButtonState: LoadingButton;
  cleverLoadingButtonState: LoadingButton;
};

type LoginScreenProps = ReturnType<typeof mapStateToProps> & RouteComponentProps;
class LoginScreen extends React.Component<LoginScreenProps, LoginState> {
  constructor(props: LoginScreenProps) {
    super(props);

    this.state = {
      userName: "",
      password: "",
      error: "",
      googleLoadingButtonState: LoadingButton.BeforeLoading,
      cleverLoadingButtonState: LoadingButton.BeforeLoading,
    };
  }

  onChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    this.setState(state => {
      return {
        ...state,
        [event.target.id]: event.target.value,
      };
    });
  }

  onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    this.signIn();
  }

  signIn() {
    FirebaseService.login(this.state.userName.toLowerCase(), this.state.password).catch(e => {
      if (e["code"] === "auth/wrong-password") {
        this.setState({ error: "Incorrect password" });
      } else if (e["code"] === "auth/user-not-found") {
        this.setState({ error: "User not found" });
      } else {
        this.setState({ error: "Server error" });
      }
    });
  }

  loginWithGoogle = () => {
    FirebaseService.loginWithGoogle();
  };

  redirectHomeWithClassCode = () => {
    const { joinClass = "" } = queryString.parse(window.location.search);
    this.props.history.replace(`/?${queryString.stringify({ joinClass })}`);
  };

  finishLoginWithGoogle = () => {
    FirebaseService.collectLoginData()
      .then(() => this.changeGoogleLoadingButtonState(LoadingButton.DoneLoading))
      .then(() => {
        this.redirectHomeWithClassCode();
      })
      .catch(e => {
        if (e?.code === "auth/cancelled-popup-request") {
          showErrorModal(
            "Could not open Google dialog",
            'It looks like you quickly clicked on the "Sign in With Google" button.  ' +
              "When this button is clicked a new window will open up to allow you to select your Google user.  You will need to complete all steps in that " +
              "window before continuing on.",
          );
        } else if (e?.message === "No account") {
          const { joinClass = "" } = queryString.parse(window.location.search);
          this.props.history.replace(`/sign-up?${queryString.stringify({ classCode: joinClass })}`);
        } else if (e?.message !== "No user") {
          LogService.exception(e, "LoginScreen.finishLoginWithGoogle", "Failed to login");
        }
        // Reset Login with Google Button
        this.changeGoogleLoadingButtonState(LoadingButton.BeforeLoading);
      });
  };

  override componentDidUpdate() {
    if (this.props.currentUserState.currentUser !== undefined) {
      this.redirectHomeWithClassCode();
    }
  }

  override componentDidMount() {
    // Check for clever code being sent to app
    const params = new URLSearchParams(this.props.location.search);
    const code = params.get("code");

    if (code) {
      this.changeCleverLoadingButtonState(LoadingButton.Loading);
      FirebaseService.loginWithClever(
        code,
        window.location.origin + window.location.pathname,
        () => this.redirectHomeWithClassCode(),
        classes =>
          this.props.history.replace("/Onboarding", {
            cleverClasses: classes,
          }),
        uid => {
          if (this.classCodeQueryParam.length > 0) FirebaseService.addUserToClass(this.classCodeQueryParam, uid);
        },
      )
        .then(() => {
          this.changeCleverLoadingButtonState(LoadingButton.DoneLoading);
        })
        .catch(error => {
          console.log(error);
          alert("Server issue, please try again later.");
          this.changeCleverLoadingButtonState(LoadingButton.BeforeLoading);
        });
    } else {
      this.finishLoginWithGoogle();
    }
  }

  // Login with Clever
  logInWithClever = () => {
    this.changeCleverLoadingButtonState(LoadingButton.Loading);
    const client_id = process.env.REACT_APP_ENV === "production" ? "7af57427e1bca7d1dc60" : "fbf100a3149f285ed38a";
    window.location.href = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${
      window.location.origin + window.location.pathname
    }&client_id=${client_id}`;
  };

  changeGoogleLoadingButtonState = (loadingState: LoadingButton) => {
    this.setState(state => {
      return {
        ...state,
        googleLoadingButtonState: loadingState,
      };
    });
  };

  changeCleverLoadingButtonState = (loadingState: LoadingButton) => {
    this.setState(state => {
      return {
        ...state,
        cleverLoadingButtonState: loadingState,
      };
    });
  };

  get classCodeQueryParam() {
    return new URLSearchParams(this.props.location.search).get("joinClass") || "";
  }

  override render() {
    const { isConnectingUser, isConnectingSettings } = this.props.currentUserState;
    if (isConnectingUser || isConnectingSettings) return <Loading />;

    return (
      <Container className="LoginScreen" style={{ backgroundColor: "white" }}>
        <div className="general">
          <form onSubmit={e => this.onSubmit(e)}>
            <Box height={80} />
            <img src={logo} alt="Readlee" height={30} />
            <Box height={35} />
            <div style={{ fontWeight: "bolder", fontSize: "larger" }}>Welcome Back! Login below</div>
            <Box height={15} />
            <div style={{ color: "red" }}>{this.state.error}</div>
            <Box height={40} />
            <TextField
              className="input-field"
              variant="outlined"
              id="userName"
              label="Email"
              onChange={e => this.onChange(e)}
              required
              inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
            />
            <Box height={10} />
            <TextField
              className="input-field"
              variant="outlined"
              id="password"
              label="Password"
              required
              type="Password"
              autoComplete="current-password"
              onChange={e => this.onChange(e)}
            />
            <Box height={40} />

            <Button variant="contained" className="LoginButton" type="submit" color="primary">
              Login
            </Button>
            <Box height={10} />
            <Button variant="text" className="clickable-text" style={{ color: "blue" }}>
              <Link className="NavLink" to="/ForgotPassword">
                Forgot password?
              </Link>
            </Button>
            <Box height={15} />
            <div className="center-contents">
              <hr className="or-style" style={{ width: "50px" }} />
              <div className="subtext">Or</div>
              <hr className="or-style" style={{ width: "50px" }} />
            </div>
            <Box height={15} />
            <ButtonWithLoading
              variant="contained"
              style={{ backgroundColor: "white", width: "250px", textTransform: "none" }}
              className="LoginButton"
              img={CleverIcon}
              imgAlt="Clever"
              imgStyles={{ height: "20px" }}
              buttonClickText={"Sign In With Clever"}
              buttonFinishedText={"Success!"}
              clickHandler={this.logInWithClever}
              loadingButtonState={this.state.cleverLoadingButtonState}
              changeLoadingButtonState={this.changeCleverLoadingButtonState}
            />
            <Box height={15} />
            <ButtonWithLoading
              variant="contained"
              style={{ backgroundColor: "white", width: "250px", textTransform: "none" }}
              className="LoginButton"
              img={GoogleIcon}
              imgAlt="Google"
              buttonClickText="Sign In With Google"
              buttonFinishedText="Success!"
              clickHandler={this.loginWithGoogle}
              loadingButtonState={this.state.googleLoadingButtonState}
              changeLoadingButtonState={this.changeGoogleLoadingButtonState}
            />
            <Box height={40} />
            <div className="center-contents">
              <div className="subtext">
                By proceeding you also agree to the &nbsp;
                <a href="https://www.readlee.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  <span className="subtext">Terms of Use</span>
                </a>
              </div>
            </div>
            <div className="center-contents">
              <div className="subtext">
                Don't have an account?
                <Button className="subtext">
                  <Link
                    className="NavLink"
                    to={`/sign-up${this.classCodeQueryParam ? `?classCode=${this.classCodeQueryParam}` : ""}`}
                  >
                    Sign up
                  </Link>
                </Button>
              </div>
            </div>
          </form>
        </div>
        <InformationModal
          title={this.props.informationModalState.title}
          onOk={closeInformationModal}
          open={this.props.informationModalState.isOpen}
        >
          {this.props.informationModalState.message}
        </InformationModal>
      </Container>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUserState: state.currentUserState,
    informationModalState: state.informationModalState,
  };
};

export default connect(mapStateToProps)(LoginScreen);
